import React, { useState, useEffect } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import useSchedulingStore from "./useSchedulingStore";
import ScheduledMessageDialog, {
  ScheduleStatusBadge,
} from "./ScheduledMessageDialog";
import RecurringScheduleDialog, {
  RecurringScheduleStatusBadge,
  formatRecurrenceDescription,
} from "./RecurringScheduleDialog";
import { formatScheduleTime } from "./agentSessionsPanelUtils";
import { useStatusPalette } from "../../utils/statusColors";

function ScheduledMessageRow({ item, onClick }) {
  const _sp = useStatusPalette();
  const preview = item.message
    ? item.message.length > 80
      ? item.message.substring(0, 80) + "..."
      : item.message
    : "Scheduled message";

  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: "divider",
        cursor: "pointer",
        "&:hover": { backgroundColor: "action.hover" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <ScheduleIcon sx={{ fontSize: 14, color: _sp.purple.color }} />
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: "text.primary",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {preview}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ScheduleStatusBadge status={item.status} />
        {item.resumeAt && item.status === "SCHEDULED" && (
          <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
            {formatScheduleTime(item.resumeAt)}
          </Typography>
        )}
        {item.reason && (
          <>
            <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
              ·
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                color: "text.secondary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 120,
              }}
            >
              {item.reason}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}

function RecurringScheduleRow({ item, onClick }) {
  const _sp = useStatusPalette();
  const preview = item.message
    ? item.message.length > 80
      ? item.message.substring(0, 80) + "..."
      : item.message
    : "Recurring schedule";

  const recDesc = formatRecurrenceDescription(item);

  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: "divider",
        cursor: "pointer",
        "&:hover": { backgroundColor: "action.hover" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <EventRepeatOutlinedIcon sx={{ fontSize: 14, color: _sp.teal.color }} />
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: "text.primary",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {preview}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <RecurringScheduleStatusBadge status={item.status} />
        <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
          {recDesc}
        </Typography>
        {item.nextResumeAt && (item.status === "ACTIVE" || item.status === "FIRING") && (
          <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
            · Next: {formatScheduleTime(item.nextResumeAt)}
          </Typography>
        )}
        {(item.occurrenceCount > 0 || item.maxOccurrences != null) && (
          <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
            · {item.occurrenceCount}{item.maxOccurrences != null ? `/${item.maxOccurrences}` : 'x'}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function ScheduledTab({ agentId }) {
  const [dialogItem, setDialogItem] = useState(null);
  const [recurringDialogItem, setRecurringDialogItem] = useState(null);
  const scheduledMessages = useSchedulingStore(state => state.scheduledMessages);
  const scheduledMessagesLoading = useSchedulingStore(state => state.scheduledMessagesLoading);
  const recurringSchedules = useSchedulingStore(state => state.recurringSchedules);
  const recurringSchedulesLoading = useSchedulingStore(state => state.recurringSchedulesLoading);

  // Fetch once on open for instant freshness. The recurring 15s poll lives in the
  // parent (ExpandedSidebar), which writes the same useSchedulingStore this tab
  // reads — a second interval here would just double the requests.
  useEffect(() => {
    if (agentId) {
      useSchedulingStore.getState().fetchScheduledMessagesByAgent(agentId);
      useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
    }
  }, [agentId]);

  const handleCancel = async (scheduleId) => {
    await useSchedulingStore.getState().cancelScheduledMessage(scheduleId);
    useSchedulingStore.getState().fetchScheduledMessagesByAgent(agentId);
  };

  const handleEdit = async (scheduleId, payload) => {
    await useSchedulingStore.getState().editScheduledMessage(scheduleId, payload);
    useSchedulingStore.getState().fetchScheduledMessagesByAgent(agentId);
  };

  const handleRecurringCancel = async (scheduleId) => {
    await useSchedulingStore.getState().cancelRecurringSchedule(scheduleId);
    useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
  };

  const handleRecurringEdit = async (scheduleId, payload) => {
    await useSchedulingStore.getState().editRecurringSchedule(scheduleId, payload);
    useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
  };

  const handleRecurringPause = async (scheduleId) => {
    await useSchedulingStore.getState().pauseRecurringSchedule(scheduleId);
    useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
  };

  const handleRecurringResume = async (scheduleId) => {
    await useSchedulingStore.getState().resumeRecurringSchedule(scheduleId);
    useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
  };

  const activeMessages = scheduledMessages.filter(
    (m) => m.status === "SCHEDULED" || m.status === "RESUMING"
  );
  const activeRecurring = recurringSchedules.filter(
    (r) => r.status === "ACTIVE" || r.status === "FIRING" || r.status === "PAUSED" || r.status === "COMPLETED"
  );

  const isLoading = (scheduledMessagesLoading && scheduledMessages.length === 0)
    || (recurringSchedulesLoading && recurringSchedules.length === 0);

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton variant="text" width="70%" height={18} />
            <Skeleton variant="text" width="40%" height={14} />
          </Box>
        ))}
      </Box>
    );
  }

  if (activeMessages.length === 0 && activeRecurring.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <ScheduleIcon sx={{ fontSize: 32, color: "text.disabled", mb: 1 }} />
        <Typography
          sx={{ fontSize: "13px", color: "text.secondary", lineHeight: 1.6 }}
        >
          No scheduled or recurring messages.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box>
        {activeRecurring.map((item) => (
          <RecurringScheduleRow
            key={`recurring-${item.scheduleId}`}
            item={item}
            onClick={() => setRecurringDialogItem(item)}
          />
        ))}
        {activeMessages.map((item) => (
          <ScheduledMessageRow
            key={item.scheduleId}
            item={item}
            onClick={() => setDialogItem(item)}
          />
        ))}
      </Box>
      <ScheduledMessageDialog
        item={dialogItem}
        open={!!dialogItem}
        onClose={() => setDialogItem(null)}
        onEdit={handleEdit}
        onCancel={handleCancel}
      />
      <RecurringScheduleDialog
        item={recurringDialogItem}
        open={!!recurringDialogItem}
        onClose={() => setRecurringDialogItem(null)}
        onEdit={handleRecurringEdit}
        onCancel={handleRecurringCancel}
        onPause={handleRecurringPause}
        onResume={handleRecurringResume}
      />
    </>
  );
}
